'use client';

import { useState } from 'react';
import { keepPreviousData } from '@tanstack/query-core';
import { PhoneCall } from 'lucide-react';

import { CallTable } from '@/app/(app)/calls/components/call-table';
import { CallFiltersProvider, useCallFiltersCtx } from '@/app/(app)/calls/components/provider';
import { PageMain } from '@/app/(app)/page-main';
import { PaginationWithEllipses } from '@/components/pagination';
import { useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Skeleton } from '@/components/ui/skeleton';

export function CallsPage() {
  return (
    <CallFiltersProvider>
      <CallHistory />
    </CallFiltersProvider>
  );
}

function CallHistory() {
  const { api } = useApiV1Ctx();
  const { officeId } = useOfficeCtx();
  const [pageSize] = useState(25);
  const { filters, setFilters } = useCallFiltersCtx();

  const { data, isFetching } = api.calls.searchCallsSearchGet.useQuery(
    {
      query: {
        office_id: officeId,
        from_number: filters.from_number,
        date__gte: filters?.date_range?.from?.toISOString(),
        date__lte: filters?.date_range?.to?.toISOString(),
        intents: filters.intents,
        outcomes: filters.outcomes,
        human_requested: filters.human_requested,
        page: filters.page,
        page_size: pageSize,
      },
    },
    {
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    }
  );

  const totalCount = data?.pagination.total || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  if (!data) {
    if (isFetching) {
      return (
        <PageMain title='Call History'>
          <Skeleton className='h-[80dvh] w-full' />
        </PageMain>
      );
    }

    return (
      <PageMain title='Call History'>
        <div className='flex h-32 w-full flex-col items-center justify-center gap-3'>
          <PhoneCall className='size-10 text-muted-foreground' />
          <div className='text-muted-foreground'>You don&apos;t have any calls yet.</div>
        </div>
      </PageMain>
    );
  }

  return (
    <PageMain title='Call History'>
      <div className='space-y-2'>
        <CallTable data={data?.calls || []} isLoading={isFetching} />

        <PaginationWithEllipses
          className='mt-4'
          currentPage={filters.page}
          totalPages={totalPages}
          onPageChange={(v) => setFilters({ page: v })}
        />
      </div>
    </PageMain>
  );
}
